import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Grid,
  Snackbar,
  Typography,
} from "@mui/material";

import ScenarioForm from "./components/ScenarioForm";
import ScenarioMap from "./components/scenarioMap/ScenarioMap";

import "./CreateScenarios.css";

import scenarioMetadata from "./data/scenarioMetadata";
import { fetchNetwork } from "./functions/fetchNetwork";
import { fetchArea } from "./functions/fetchArea";
import { fetchRoutes } from "./functions/fetchRoutes";
import axios from "axios";
import config from "../../../config";
import {decodeJwt } from "../../../authentication/authModules";

function CreateScenarios() {
  const { t } = useTranslation();
  const SYNCHROEU_URL = config.SYNCHROEU_URL;
  const token = localStorage.getItem("keycloak-token");
  let email = "info@toolbox.synchromode.eu";
  let userId = "anonymous"; 
  if (token) {
    try {
      const tokenObject = decodeJwt(token);
      //console.log("Decoded Token:", tokenObject);
      email = tokenObject?.email || "info@toolbox.synchromode.eu"; // Extract userId from token
      userId = tokenObject?.sub || "anonymous"; // Extract userId from token
    } catch (error) {
      console.error("Error decoding JWT:", error);
    }
  }

  const navigate = useNavigate();
  const redirectTimerRef = useRef(null);

  const [metadata, setMetadata] = useState(null);
  const [loadingMetadata, setLoadingMetadata] = useState(true);

  const [monitoringItems, setMonitoringItems] = useState({
    type: null,
    items: [],
  });

  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [submitting, setSubmitting] = useState(false);

  const [scenarioData, setScenarioData] = useState({
    category_id: null,
    name: "",
    description: "",
    start_date: null,
    end_date: null,
    kpi_id: null,
    kpi_option_id: null,
    kpi_monitoring_ids: [],
    type_ids: null,
    location: null,
    location_path_ids: [],
    action_id: null,
    target_ids: [],
    message: "",
    created_by:"anonymous"
  });

  const [network, setNetwork] = useState(null);
  const [loadingNetwork, setLoadingNetwork] = useState(true);
  const [routesData, setRoutesData] = useState([]);
  const [loadingRoutesData, setLoadingRoutesData] = useState(true);
  const [areasData, setAreasData] = useState([]);
  const [loadingAreasData, setLoadingAreasData] = useState(true);

  const handleScenarioChange = (patch) => {
    setScenarioData((prev) => ({ ...prev, ...patch }));
  };

  // ✅ Convert ANY error payload into a string (prevents "Objects are not valid as a React child")
  const normalizeErrorMessage = (err) => {
    const fallback = t("scenarios.create.submitFailFallback");

    const data = err?.response?.data;

    // string directly
    if (typeof data === "string") return data;

    // common fields
    const common =
      data?.message || data?.detail || data?.error || data?.title || data?.msg;
    if (typeof common === "string") return common;

    // FastAPI / Pydantic validation (array of {loc,msg,...} or object containing it)
    const validationArray =
      Array.isArray(data) ? data : Array.isArray(data?.detail) ? data.detail : null;

    if (validationArray) {
      // Show first few errors
      return validationArray
        .slice(0, 3)
        .map((e) => {
          const loc = Array.isArray(e?.loc) ? e.loc.join(".") : e?.loc;
          const msg = e?.msg || e?.message || t("scenarios.create.validationError");
          return loc ? `${loc}: ${msg}` : `${msg}`;
        })
        .join(" | ");
    }

    // last resort: stringify safely
    try {
      if (data != null) return JSON.stringify(data);
    } catch {
      // ignore
    }

    return fallback;
  };

  // metadata
  useEffect(() => {
    setMetadata(scenarioMetadata);
    setLoadingMetadata(false);
  }, []);

  // network
  useEffect(() => {
    let cancelled = false;

    async function loadNetwork() {
      try {
        const data = await fetchNetwork();
        if (!cancelled) setNetwork(data);
      } finally {
        if (!cancelled) setLoadingNetwork(false);
      }
    }

    loadNetwork();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadRoutes() {
      try {
        const data = await fetchRoutes();
        if (!cancelled) setRoutesData(data);
      } catch (error) {
        if (!cancelled) setRoutesData([]);
      } finally {
        if (!cancelled) setLoadingRoutesData(false);
      }
    }

    loadRoutes();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadAreas() {
      try {
        const data = await fetchArea();
        if (!cancelled) setAreasData(data);
      } catch (error) {
        if (!cancelled) setAreasData([]);
      } finally {
        if (!cancelled) setLoadingAreasData(false);
      }
    }

    loadAreas();
    return () => {
      cancelled = true;
    };
  }, []);

  // cleanup redirect timer
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  const handleSubmit = async (payload) => {
    if (submitting) return;
    setSubmitting(true);

    let requestPayload =  {
          category_id: payload.category_id,
          name: payload.name,
          description: payload.description,
          start_date: payload.start_date,
          end_date: payload.end_date,
          kpi_id: payload.kpi_id,
          kpi_option_id: payload.kpi_option_id,
          kpi_monitoring_ids: payload.kpi_monitoring_ids,
          location: payload.location,
          location_path_ids: payload.location_path_ids,
          action_id: payload.action_id,
          target_ids: payload.target_ids,
          type_ids: payload.type_ids,
          message: payload.message,
          created_by: userId,
        };

    try {
      await axios.post(
        `${SYNCHROEU_URL}/scenarios/create`,
    requestPayload,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      setAlertState({
        open: true,
        message: t("scenarios.create.submitSuccess"),
        severity: "success",
      });

      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = setTimeout(() => {
        navigate("/scenariosLibrary", { replace: true });
      }, 2000);
    } catch (error) {
      setAlertState({
        open: true,
        message: normalizeErrorMessage(error),
        severity: "error",
      });
      console.error("Error submitting form:", error);
    } finally {
      setSubmitting(false);
    }
    console.log("Submitted payload:", requestPayload);
    console.log("Submitted payload stringify:", JSON.stringify(requestPayload));
  };

  return (
    <DashboardLayout>
      {/* Snackbar */}
      <Snackbar
        open={alertState.open}
        autoHideDuration={5000}
        onClose={() => setAlertState((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={alertState.severity}
          variant="filled"
          onClose={() => setAlertState((s) => ({ ...s, open: false }))}
        >
          {String(alertState.message)}
        </Alert>
      </Snackbar>

      {/* Request loader overlay */}
      <Backdrop
        open={submitting}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2000 }}
      >
        <Grid container direction="column" alignItems="center" gap={2}>
          <CircularProgress />
          <Typography variant="body2">{t("scenarios.create.submittingScenario")}</Typography>
        </Grid>
      </Backdrop>

      {/* Initial loading */}
      {loadingMetadata || loadingNetwork || loadingRoutesData || loadingAreasData ? (
        <div className="create-scenarios-spinner">
          <CircularProgress />
        </div>
      ) : (
        <Card id="scenario-form">
          <Grid container spacing={2} sx={{ width: "100%", m: 0, p: 0 }}>
            <Grid item xs={12} md={4} lg={3}>
              <ScenarioForm
                value={scenarioData}
                onChange={handleScenarioChange}
                metadata={metadata}
                onSubmit={handleSubmit}
                onMonitoringItemsChange={setMonitoringItems}
                routesData={routesData}
                areasData={areasData}
                submitting={submitting}
              />
            </Grid>

            <Grid item xs={12} md={8} lg={9} sx={{ p: 0 }}>
              <ScenarioMap
                value={scenarioData}
                onChange={handleScenarioChange}
                network={network}
                monitoringItems={monitoringItems}
                routesData={routesData}
                areasData={areasData}
              />
            </Grid>
          </Grid>
        </Card>
      )}
    </DashboardLayout>
  );
}

export default CreateScenarios;
